/**
 * MigrateStrategyConfigButton — shown when a strategy config is stale.
 *
 * Flow:
 * 1. Button "Migrate to vN" appears only when isStale=true.
 * 2. Click opens a confirmation modal explaining the migration is NON-DESTRUCTIVE.
 * 3. Confirm → POST /api/strategy-configs/:id/migrate { toVersion: N }.
 * 4. Success → shows dropped/added keys and calls onSuccess(newConfigId).
 * 5. Error → shows the error message in-modal.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { migrateStrategyConfig } from '../../api/client.js';
import type { MigrateStrategyConfigResponse } from '../../types.js';

interface MigrateStrategyConfigButtonProps {
  configId: string;
  /** The current (latest) version to migrate to. */
  strategyCurrentVersion?: number | null;
  /** Only render the button when true. */
  isStale?: boolean;
  /** Called with the new config ID after a successful migration. */
  onSuccess: (newConfigId: string) => void;
}

type ModalState =
  | { phase: 'closed' }
  | { phase: 'confirm' }
  | { phase: 'success'; result: MigrateStrategyConfigResponse }
  | { phase: 'error'; message: string };

export function MigrateStrategyConfigButton({
  configId,
  strategyCurrentVersion,
  isStale,
  onSuccess,
}: MigrateStrategyConfigButtonProps) {
  const [modal, setModal] = useState<ModalState>({ phase: 'closed' });

  const migrateMutation = useMutation({
    mutationFn: () => {
      if (strategyCurrentVersion == null) {
        throw new Error('Target version is unknown');
      }
      return migrateStrategyConfig(configId, strategyCurrentVersion);
    },
    onSuccess: (result) => {
      setModal({ phase: 'success', result });
      onSuccess(result.newConfigId);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      setModal({ phase: 'error', message });
    },
  });

  if (!isStale) return null;

  const targetVersion = strategyCurrentVersion;

  return (
    <>
      <button
        onClick={() => setModal({ phase: 'confirm' })}
        style={{
          padding: '4px 12px',
          background: 'rgba(250,204,21,0.10)',
          border: '1px solid rgba(250,204,21,0.35)',
          borderRadius: 5,
          color: '#facc15',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.1s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(250,204,21,0.18)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(250,204,21,0.10)';
        }}
      >
        Migrate to v{targetVersion}
      </button>

      {modal.phase !== 'closed' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => {
            if (modal.phase !== 'success') setModal({ phase: 'closed' });
          }}
        >
          <div
            style={{
              background: '#1e1e1e',
              border: '1px solid #3a3a3a',
              borderRadius: 10,
              padding: '28px 32px',
              maxWidth: 480,
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {modal.phase === 'confirm' && (
              <ConfirmPanel
                targetVersion={targetVersion}
                isPending={migrateMutation.isPending}
                onConfirm={() => migrateMutation.mutate()}
                onCancel={() => setModal({ phase: 'closed' })}
              />
            )}

            {modal.phase === 'success' && (
              <SuccessPanel
                result={modal.result}
                onClose={() => setModal({ phase: 'closed' })}
              />
            )}

            {modal.phase === 'error' && (
              <ErrorPanel
                message={modal.message}
                onClose={() => setModal({ phase: 'closed' })}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Sub-panels ─────────────────────────────────────────────────────────────

function ConfirmPanel({
  targetVersion,
  isPending,
  onConfirm,
  onCancel,
}: {
  targetVersion: number | null | undefined;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: '#e0e0e0' }}>
        Migrate to v{targetVersion}?
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#aaa', lineHeight: 1.6 }}>
        Migrate creates a new pinned configuration at v{targetVersion}. The original config
        is <strong style={{ color: '#e0e0e0' }}>kept untouched</strong> — all existing backtest
        runs will still reference the old config.
      </p>
      <p style={{ margin: '0 0 20px', fontSize: 12, color: '#777', lineHeight: 1.5 }}>
        Parameters removed in the new schema will be dropped from the migrated config.
        You will see which params were dropped or added after confirming.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          disabled={isPending}
          style={{
            padding: '8px 18px',
            background: '#2a2a2a',
            border: '1px solid #444',
            borderRadius: 6,
            color: '#ccc',
            fontSize: 13,
            cursor: isPending ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isPending}
          style={{
            padding: '8px 18px',
            background: 'rgba(250,204,21,0.15)',
            border: '1px solid rgba(250,204,21,0.4)',
            borderRadius: 6,
            color: '#facc15',
            fontSize: 13,
            fontWeight: 600,
            cursor: isPending ? 'not-allowed' : 'pointer',
            opacity: isPending ? 0.6 : 1,
          }}
        >
          {isPending ? 'Migrating...' : 'Confirm'}
        </button>
      </div>
    </>
  );
}

function SuccessPanel({
  result,
  onClose,
}: {
  result: MigrateStrategyConfigResponse;
  onClose: () => void;
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <svg width="20" height="20" fill="none" stroke="#4ade80" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e0e0e0' }}>
          Migration complete — v{result.strategyVersion}
        </h3>
      </div>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>
        New config ID: <code style={{ color: '#93c5fd', fontSize: 11 }}>{result.newConfigId}</code>
      </p>
      {result.dropped.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: '#f87171' }}>
            Dropped params ({result.dropped.length}):
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#fca5a5' }}>
            {result.dropped.map((k) => <li key={k}>{k}</li>)}
          </ul>
        </div>
      )}
      {result.added.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: '#4ade80' }}>
            Added params ({result.added.length}):
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#86efac' }}>
            {result.added.map((k) => <li key={k}>{k}</li>)}
          </ul>
        </div>
      )}
      {result.dropped.length === 0 && result.added.length === 0 && (
        <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          No param changes — all parameters are compatible with the new version.
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 18px',
            background: '#2a2a2a',
            border: '1px solid #444',
            borderRadius: 6,
            color: '#ccc',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </>
  );
}

function ErrorPanel({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <svg width="20" height="20" fill="none" stroke="#f87171" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e0e0e0' }}>
          Migration failed
        </h3>
      </div>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#fca5a5' }}>{message}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 18px',
            background: '#2a2a2a',
            border: '1px solid #444',
            borderRadius: 6,
            color: '#ccc',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </>
  );
}
